import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../api/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SprintStatus = 'planned' | 'active' | 'paused' | 'completed' | 'cancelled';
export type SprintType = 'strict' | 'fluid';

export interface Sprint {
  _id: string;
  orgId?: string;
  projectId: string;
  projectName: string;
  name: string;
  description?: string;
  sprintType: SprintType;
  status: SprintStatus;
  capacity?: number;
  timeline: {
    startDate: string;
    endDate?: string;
    actualEndDate?: string;
    overriddenEndDate?: string;
    durationDays?: number;
  };
  roles: {
    userId: string;
    userName: string;
    role: 'owner' | 'editor' | 'viewer';
    assignedAt: string;
  }[];
  stats: {
    totalTasks: number;
    completedTasks: number;
    completedStoryPoints: number;
  };
  velocity?: number;
  pausePeriods: any[];
  breachHistory: any[];
  amendmentLog: any[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface IncompleteItem {
  _id: string;
  type: 'task' | 'feature' | 'bug';
  title: string;
  status: string;
  priority?: string;
  dueDate?: string;
}

interface SprintState {
  sprints: Sprint[];
  currentSprint: Sprint | null;
  board: {
    todo: any[];
    in_progress: any[];
    blocked: any[];
    review: any[];
    done: any[];
  } | null;
  features: any[];
  incompleteItems: IncompleteItem[];
  loading: boolean;
  error: string | null;
}

const initialState: SprintState = {
  sprints: [],
  currentSprint: null,
  board: null,
  features: [],
  incompleteItems: [],
  loading: false,
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchSprintsByProject = createAsyncThunk(
  'sprints/fetchByProject',
  async ({ projectId, status }: { projectId: string; status?: string }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/sprints/project/${projectId}`, { params: { status } });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch sprints');
    }
  }
);

export const fetchSprintById = createAsyncThunk(
  'sprints/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/sprints/${id}`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch sprint');
    }
  }
);

export const fetchSprintBoard = createAsyncThunk(
  'sprints/fetchBoard',
  async ({ id, subprojectId }: { id: string; subprojectId?: string }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/sprints/${id}/board`, { params: { subprojectId } });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch sprint board');
    }
  }
);

export const createSprint = createAsyncThunk(
  'sprints/create',
  async (data: Partial<Sprint> & { projectId: string }, { rejectWithValue }) => {
    try {
      const res = await api.post('/sprints', data);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create sprint');
    }
  }
);

export const updateSprint = createAsyncThunk(
  'sprints/update',
  async ({ id, data }: { id: string; data: Partial<Sprint> }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/sprints/${id}`, data);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update sprint');
    }
  }
);

export const deleteSprint = createAsyncThunk(
  'sprints/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/sprints/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete sprint');
    }
  }
);

export const startSprint = createAsyncThunk(
  'sprints/start',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.post(`/sprints/${id}/start`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to start sprint');
    }
  }
);

export const completeSprint = createAsyncThunk(
  'sprints/complete',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.post(`/sprints/${id}/complete`);
      // Returns { data: sprint, summary, incompleteItems }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to complete sprint');
    }
  }
);

export const resolveIncompleteItem = createAsyncThunk(
  'sprints/resolveIncomplete',
  async (
    {
      sprintId,
      itemId,
      itemType,
      action,
      nextSprintId,
      newDeadline,
      reason,
      measuresTaken,
    }: {
      sprintId: string;
      itemId: string;
      itemType: 'task' | 'feature' | 'bug';
      action: 'backlog' | 'next_sprint' | 'extend';
      nextSprintId?: string;
      newDeadline?: string;
      reason?: string;
      measuresTaken?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await api.post(`/sprints/${sprintId}/resolve-incomplete`, {
        itemId, itemType, action, nextSprintId, newDeadline, reason, measuresTaken,
      });
      return itemId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to resolve item');
    }
  }
);

export const pauseSprint = createAsyncThunk(
  'sprints/pause',
  async ({ id, reason, pauseScope }: { id: string; reason?: string; pauseScope?: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/sprints/${id}/pause`, { reason, pauseScope });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to pause sprint');
    }
  }
);

export const resumeSprint = createAsyncThunk(
  'sprints/resume',
  async ({ id, deadlinesAdjusted }: { id: string; deadlinesAdjusted?: boolean }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/sprints/${id}/resume`, { deadlinesAdjusted });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to resume sprint');
    }
  }
);

export const addItemToSprint = createAsyncThunk(
  'sprints/addItem',
  async ({ id, itemId, itemType }: { id: string; itemId: string; itemType: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/sprints/${id}/items`, { itemId, itemType });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add item');
    }
  }
);

export const removeItemFromSprint = createAsyncThunk(
  'sprints/removeItem',
  async ({ id, itemId, itemType }: { id: string; itemId: string; itemType: string }, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/sprints/${id}/items`, { data: { itemId, itemType } });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove item');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const sprintSlice = createSlice({
  name: 'sprints',
  initialState,
  reducers: {
    clearSprintError: (state) => { state.error = null; },
    clearCurrentSprint: (state) => { state.currentSprint = null; state.board = null; },
    updateSprintInList: (state, action: PayloadAction<Sprint>) => {
      const idx = state.sprints.findIndex(s => s._id === action.payload._id);
      if (idx !== -1) state.sprints[idx] = action.payload;
    },
  },
  extraReducers: (builder) => {
    const pending = (state: SprintState) => { state.loading = true; state.error = null; };
    const rejected = (state: SprintState, action: any) => {
      state.loading = false;
      state.error = action.payload as string;
    };

    builder
      .addCase(fetchSprintsByProject.pending, pending)
      .addCase(fetchSprintsByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints = action.payload;
      })
      .addCase(fetchSprintsByProject.rejected, rejected)

      .addCase(fetchSprintById.pending, pending)
      .addCase(fetchSprintById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSprint = action.payload;
      })
      .addCase(fetchSprintById.rejected, rejected)

      .addCase(fetchSprintBoard.pending, (state) => {
        // Only show loading on initial fetch, not on silent re-fetches
        if (!state.board) { state.loading = true; state.error = null; }
      })
      .addCase(fetchSprintBoard.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSprint = action.payload.sprint;
        state.board = action.payload.board;
        state.features = action.payload.features ?? [];
      })
      .addCase(fetchSprintBoard.rejected, rejected)

      .addCase(createSprint.pending, pending)
      .addCase(createSprint.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints.unshift(action.payload);
      })
      .addCase(createSprint.rejected, rejected)

      .addCase(updateSprint.pending, pending)
      .addCase(updateSprint.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.sprints.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.sprints[idx] = action.payload;
        if (state.currentSprint?._id === action.payload._id) state.currentSprint = action.payload;
      })
      .addCase(updateSprint.rejected, rejected)

      .addCase(deleteSprint.pending, pending)
      .addCase(deleteSprint.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints = state.sprints.filter(s => s._id !== action.payload);
      })
      .addCase(deleteSprint.rejected, rejected)

      .addCase(startSprint.fulfilled, (state, action) => {
        const idx = state.sprints.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.sprints[idx] = action.payload;
        if (state.currentSprint?._id === action.payload._id) state.currentSprint = action.payload;
      })
      .addCase(completeSprint.fulfilled, (state, action) => {
        const sprint = action.payload.data;
        const idx = state.sprints.findIndex(s => s._id === sprint._id);
        if (idx !== -1) state.sprints[idx] = sprint;
        if (state.currentSprint?._id === sprint._id) state.currentSprint = sprint;
        state.incompleteItems = action.payload.incompleteItems ?? [];
      })
      .addCase(resolveIncompleteItem.fulfilled, (state, action) => {
        state.incompleteItems = state.incompleteItems.filter(i => i._id !== action.payload);
      })
      .addCase(pauseSprint.fulfilled, (state, action) => {
        const idx = state.sprints.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.sprints[idx] = action.payload;
        if (state.currentSprint?._id === action.payload._id) state.currentSprint = action.payload;
      })
      .addCase(resumeSprint.fulfilled, (state, action) => {
        const idx = state.sprints.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.sprints[idx] = action.payload;
        if (state.currentSprint?._id === action.payload._id) state.currentSprint = action.payload;
      });
  },
});

export const { clearSprintError, clearCurrentSprint, updateSprintInList } = sprintSlice.actions;
export default sprintSlice.reducer;
